import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { blogPosts } from './BlogPage';
import CallToAction from '../components/CallToAction';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  const others = blogPosts.filter(p => p.slug !== slug).slice(0, 2);

  if (!post) return (
    <main className="pt-32 text-center py-20">
      <h1 className="text-3xl font-serif font-bold text-stone-900 mb-4">Article not found</h1>
      <Link to="/blog" className="text-brand-600 font-semibold hover:underline">← Back to Journal</Link>
    </main>
  );

  return (
    <main className="pt-24">
      <section className="py-16 bg-stone-50">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Link to="/blog" className="text-brand-600 text-sm font-semibold hover:underline mb-8 inline-block">← Back to Journal</Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-600 bg-brand-50 px-3 py-1 rounded-full">{post.category}</span>
              <span className="text-stone-400 text-xs">{post.date} · {post.readTime}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mb-6 leading-tight">{post.title}</h1>
            <p className="text-xl text-stone-600 font-serif italic mb-8 leading-relaxed">{post.excerpt}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="aspect-[16/9] rounded-2xl overflow-hidden mb-10">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="prose prose-stone prose-lg max-w-none">
            {post.body.split('\n\n').map((para, i) => (
              <p key={i} className="text-stone-700 leading-relaxed text-lg mb-5 font-serif">{para}</p>
            ))}
          </motion.div>

          <div className="mt-12 p-8 bg-stone-900 rounded-2xl text-white text-center">
            <p className="font-serif italic text-lg mb-4">Explore these ideas further in the book.</p>
            <a href="https://www.amazon.com/dp/B0GHP512XW" target="_blank" rel="noopener noreferrer"
              className="inline-block px-6 py-3 bg-white text-stone-900 rounded-full font-semibold hover:bg-brand-50 transition-colors">
              Get The Silent Language of Love
            </a>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-8">More from the Journal</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {others.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`}
                  className="group bg-stone-50 rounded-2xl overflow-hidden border border-stone-100 hover:shadow-lg transition-shadow duration-300 flex flex-col">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6 flex-1">
                    <span className="text-xs font-bold uppercase tracking-widest text-brand-600 mb-2 block">{p.category}</span>
                    <h3 className="text-lg font-serif font-bold text-stone-900 group-hover:text-brand-700 transition-colors">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <div id="get-copy"><CallToAction /></div>
    </main>
  );
};

export default BlogPostPage;